import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { FilePlus2, ListChecks, Settings2, Moon, Sun, LogOut, PackageCheck, Scissors, LayoutDashboard } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useReviewCount } from "../hooks/useReviewCount";

function useTheme() {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("mm-theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
}

const NAV = [
  { to: "/home", label: "Home", icon: LayoutDashboard, id: "nav-home" },
  { to: "/new", label: "New PO", icon: FilePlus2, id: "nav-new-po", perm: ["po_write"] },
  { to: "/pos", label: "Register", icon: ListChecks, id: "nav-register", perm: ["po_view"] },
  { to: "/grns", label: "GRN", icon: PackageCheck, id: "nav-grn", perm: ["grn_view"] },
  { to: "/jobs", label: "Jobs", icon: Scissors, id: "nav-jobs", perm: ["jobs_view", "stock_view"], stockLabel: "Stock" },
  { to: "/setup", label: "Setup", icon: Settings2, id: "nav-setup" },
];

const ReviewBadge = () => {
  const reviewCount = useReviewCount();
  if (!reviewCount) return null;
  return (
    <span className="absolute -right-0.5 -top-1 grid h-5 min-w-[20px] place-items-center rounded-full bg-destructive px-1 text-[11px] font-bold text-white"
      data-testid="nav-review-badge">{reviewCount}</span>
  );
};

export const Layout = () => {
  const [dark, setDark] = useTheme();
  const { user, logout, can } = useAuth();
  const nav = NAV.filter((n) => !n.perm || n.perm.some(can));
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b bg-background/90 backdrop-blur-lg">
        <div className="mx-auto flex h-14 max-w-3xl items-center gap-3 px-4">
          <div className="flex min-w-0 flex-1 items-baseline gap-2">
            <span className="font-heading text-lg font-bold tracking-tight">Man&nbsp;&amp;&nbsp;Moda</span>
            <span className="mono-label truncate text-muted-foreground" data-testid="header-app-subtitle">ERP</span>
          </div>
          <span className="hidden text-sm text-muted-foreground sm:inline" data-testid="header-user-name">{user?.name}</span>
          <button className="icon-btn" onClick={() => setDark(!dark)} aria-label="Toggle theme" data-testid="theme-toggle-btn">
            {dark ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <button className="icon-btn" onClick={logout} aria-label="Log out" data-testid="logout-btn">
            <LogOut size={20} />
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 pb-48 pt-5">
        <Outlet />
      </main>
      <nav className="fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 backdrop-blur-lg pb-[env(safe-area-inset-bottom)]">
        <div className="mx-auto grid h-16 max-w-3xl" style={{ gridTemplateColumns: `repeat(${nav.length}, minmax(0, 1fr))` }}>
          {nav.map(({ to, label, icon: Icon, id, stockLabel }) => (
            <NavLink key={to} to={to} data-testid={id}
              className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 text-xs font-semibold transition-colors ${isActive ? "text-primary" : "text-muted-foreground"}`}>
              {({ isActive }) => (
                <>
                  <span className={`relative rounded-full px-2.5 py-1 transition-colors ${isActive ? "bg-accent-soft" : ""}`}>
                    <Icon size={22} />
                    {id === "nav-register" && <ReviewBadge />}
                  </span>
                  {stockLabel && !can("jobs_view") ? stockLabel : label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};
